import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  LanguageInterface,
  MultiCountryTotalCompletesHeader,
  SelectedCountryInterface,
} from '../shared/interfaces/create-surveys.interface';
import { QuotaPayload } from '../shared/interfaces/quota.interface';
import {
  Qualification,
  QualificationPayload,
} from '../shared/interfaces/qualification.interface';
import { QUALIFICATIONS_CATEGORIES } from '../shared/constants/qualification-categories';
import { Constants } from '../shared/constants/create-survey.constants';

@Injectable({
  providedIn: 'root',
})
export class CreateSurveyService {
  constructor() {}

  private _defaultMultiCountryData: MultiCountryData = {
    selectedCountries: [],
    selectedLanguages: [],
    isDraft: false,
    expectedIr: 0,
    clickBalancing: 0,
    completesRequired: 0,
    expectedLoi: 0,
    fieldTime: 0,
    source: 'platform',
    surveyCategoryCode: 0,
    surveyTitle: '',
    surveyId: 0,
    qualifications: [],
    payloadQualifications: [],
    quotas: [],
  };

  private _multiCountryData$ = new BehaviorSubject<MultiCountryData>(
    this._defaultMultiCountryData
  );
  private _isDraftSaved = new BehaviorSubject<boolean>(false);
  private _multiCountryTotalCompletes$ =
    new BehaviorSubject<MultiCountryTotalCompletesHeader>(
      Constants.MULTICOUNTRY.COMPLETES_HEADER_DEFAULT
    );

  private _isPurePriceApplied = new BehaviorSubject<boolean>(false);
  private _isManualProcessingPermission = new BehaviorSubject<boolean>(true);

  setDefaultMultiCountryData() {
    this._multiCountryData$.next(this._defaultMultiCountryData);
  }

  setMultiCountryData(data: MultiCountryData) {
    this._multiCountryData$.next(data);
  }

  getMultiCountryData(): Observable<MultiCountryData> {
    return this._multiCountryData$.asObservable();
  }

  resetMultiCountryData() {
    this.setDefaultMultiCountryData();
    this._isDraftSaved.next(false);
    this._multiCountryTotalCompletes$.next(
      Constants.MULTICOUNTRY.COMPLETES_HEADER_DEFAULT
    );
  }

  setMultiCountryTotalCompletesDefaultValue() {
    this._multiCountryTotalCompletes$.next(
      Constants.MULTICOUNTRY.COMPLETES_HEADER_DEFAULT
    );
  }

  setMultiCountryTotalCompletes(data: MultiCountryTotalCompletesHeader) {
    this._multiCountryTotalCompletes$.next(data);
  }

  getMultiCountryTotalCompletes() {
    return this._multiCountryTotalCompletes$.asObservable();
  }

  setIsPurePriceApplied(value: boolean) {
    this._isPurePriceApplied.next(value);
  }

  getIsPurePrieApplied(): Observable<boolean> {
    return this._isPurePriceApplied.asObservable();
  }

  setIsManualProcessingPermission(value: boolean) {
    this._isManualProcessingPermission.next(value);
  }

  getIsManualProcessingPermission(): Observable<boolean> {
    return this._isManualProcessingPermission.asObservable();
  }

  addQuotaCategory(quotas: QuotaPayload[]) {
    return quotas.map((quota) => {
      const criteria = quota.criteria[0];
      const conditions =
        (criteria.condition_codes?.length || criteria.range_sets?.length) ?? 0;

      if (conditions == 1 && !quota.quota_category) {
        quota.quota_category = QUALIFICATIONS_CATEGORIES.LAYERED;
      } else if (conditions > 1 && !quota.quota_category) {
        quota.quota_category = QUALIFICATIONS_CATEGORIES.GROUPED;
      }
      return quota;
    });
  }
}

export interface MultiCountryData {
  selectedCountries: SelectedCountryInterface[];
  selectedLanguages: LanguageInterface[];
  isDraft: boolean;
  expectedIr: number;
  clickBalancing: number;
  completesRequired: number;
  expectedLoi: number;
  fieldTime: number;
  source: string;
  surveyCategoryCode: number;
  surveyTitle: string;
  surveyId: number;
  qualifications: Array<Qualification>;
  payloadQualifications: Array<QualificationPayload>;
  quotas: Array<QuotaPayload>;
  psSurveystatus?: number;
  offerPrice?: number;
  buyerMargin?: number;
}
